/**
 * Rewrites the visible breadcrumbs to follow the route the shopper actually took.
 *
 * custom.collection_subcollections is a DAG - the same collection can hang under
 * several parents - so the server-rendered (canonical) trail sometimes names a
 * different parent than the card that was just clicked. Liquid cannot read the
 * referrer, so the walked path is rebuilt here from a sessionStorage trail.
 *
 * The canonical markup stays authoritative. It is only replaced when the
 * previous page is a legitimate parent of this one, as declared by the server in
 * `allowedPrev`; anything else leaves the server output alone. The
 * BreadcrumbList JSON-LD is never touched - structured data keeps describing the
 * canonical hierarchy.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'kuala:breadcrumb-trail';

  var nav = document.querySelector('nav.breadcrumbs');
  var contextEl = document.querySelector('[data-breadcrumb-trail-context]');
  if (!nav || !contextEl) return;

  var context;
  try {
    context = JSON.parse(contextEl.textContent);
  } catch (e) {
    return;
  }
  if (!context || !context.allowedPrev) return;

  var homeLink = nav.querySelector('a');
  if (!homeLink) return;

  function readTrail() {
    try {
      var stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      return Array.isArray(stored) ? stored : [];
    } catch (e) {
      return [];
    }
  }

  function writeTrail(trail) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
    } catch (e) {
      /* private mode, quota - the canonical trail still renders */
    }
  }

  /* Handle of the collection page the shopper came from, same origin only. */
  function previousCollectionHandle() {
    if (!document.referrer) return null;
    var referrer;
    try {
      referrer = new URL(document.referrer);
    } catch (e) {
      return null;
    }
    if (referrer.origin !== window.location.origin) return null;
    var match = referrer.pathname.match(/\/collections\/([^/]+)/);
    if (!match) return null;
    /* /collections/x/products/y is a product page, not a click on the listing */
    if (referrer.pathname.indexOf('/products/') !== -1) return null;
    return decodeURIComponent(match[1]);
  }

  /* The collections already rendered as links by Liquid, in order. */
  function canonicalCollections() {
    var out = [];
    var links = nav.querySelectorAll('a');
    for (var i = 1; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      var match = href.match(/\/collections\/([^/?#]+)/);
      if (!match) continue;
      out.push({
        handle: decodeURIComponent(match[1]),
        title: (links[i].textContent || '').trim(),
        url: href
      });
    }
    return out;
  }

  function findEntry(list, handle) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle === handle) return list[i];
    }
    return null;
  }

  function separator(className) {
    var span = document.createElement('span');
    span.setAttribute('aria-hidden', 'true');
    span.className = className;
    return span;
  }

  function render(linkEntries, lastText, lastSeparatorClass) {
    while (homeLink.nextSibling) {
      nav.removeChild(homeLink.nextSibling);
    }
    linkEntries.forEach(function (entry) {
      nav.appendChild(separator('breadcrumbs--sep'));
      var link = document.createElement('a');
      link.className = 'reversed-link';
      link.href = entry.url;
      link.textContent = entry.title;
      nav.appendChild(link);
    });
    nav.appendChild(separator(lastSeparatorClass));
    var last = document.createElement('span');
    last.className = 'breadcrumbs--last text-subtext';
    last.textContent = lastText;
    nav.appendChild(last);
  }

  var canonical = canonicalCollections();
  var previousHandle = previousCollectionHandle();
  var stored = readTrail();
  var walked = null;

  if (previousHandle) {
    var allowed = findEntry(context.allowedPrev, previousHandle);
    if (allowed) {
      /* Continue the recorded trail if it already passes through that parent,
         otherwise start a fresh two-level one from it. */
      var base = null;
      for (var i = stored.length - 1; i >= 0; i--) {
        if (stored[i] && stored[i].handle === previousHandle) {
          base = stored.slice(0, i + 1);
          break;
        }
      }
      if (!base) base = [allowed];
      if (!context.self || !findEntry(base, context.self.handle)) {
        walked = base;
      }
    }
  }

  var collectionsTrail = walked || canonical;
  var storable = collectionsTrail.slice();
  if (context.self) storable.push(context.self);
  writeTrail(storable);

  if (!walked) return;

  /* The collection-list "up one level" card is the same affordance as the last
     breadcrumb, so it has to name the same parent, not the canonical one. That
     card lives further down the page: this script runs inline, right after the
     breadcrumbs, so the slider is not parsed yet and the update has to wait. */
  if (context.kind === 'collection' && collectionsTrail.length) {
    var parentEntry = collectionsTrail[collectionsTrail.length - 1];
    var syncBackSlide = function () {
      var backLink = document.querySelector('[data-collection-back-link]');
      if (!backLink) return;
      backLink.setAttribute('href', parentEntry.url);
      var backLabel = document.querySelector('[data-collection-back-label]');
      if (backLabel) backLabel.textContent = parentEntry.title;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', syncBackSlide);
    } else {
      syncBackSlide();
    }
  }

  var separators = nav.querySelectorAll('.breadcrumbs--sep');
  var lastSeparatorClass = separators.length
    ? separators[separators.length - 1].className
    : 'breadcrumbs--sep';

  render(collectionsTrail, context.leafTitle, lastSeparatorClass);
})();
